import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { Observable, Subject, map, startWith } from "rxjs";
import { CommonModule } from "@angular/common";
import { MatAutocompleteModule, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { scaleInOutAnimation } from "src/app/shared/animations/scale-in-out.animation";
import { IconComponent } from "../../../icon/icon.component";

export interface Group {
  groupValue: string;
  groupName: string;
  groupOptions: Option[];
  bVisible?: boolean;
}

export interface Option {
  sCodigo: string;
  sDescripcion: string;
  sTipo_Configuracion: string;
}

@Component({
  selector: "mg-input-select-group",
  templateUrl: "./select-group.component.html",
  styleUrls: ["./select-group.component.scss"],
  animations: [scaleInOutAnimation],
  standalone: true,
  imports: [
    CommonModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    IconComponent
  ],
})
export class SelectGroupComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() inputControl: FormControl = new FormControl(null);
  @Input() label = "Input";
  @Input() placeholder = "";
  @Input() aOpciones?: any;
  @Input() required = false;
  @Input() mode: "create" | "read" | "update";
  @Input() isLoading = false;
  @Input() withLabel = true;
  @Input() hint_active = false;
  @Input() hint_message = "";
  @Input() firstValue?: boolean = false;
  @Input() optionIcon = false;

  opcionesfiltradas: Observable<any>;
  isScreenXS$ = screen.width <= 460;

  private readonly destroy$ = new Subject<void>();

  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly el: ElementRef,
    private readonly ngZone: NgZone,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.cdr.detectChanges();
    if (changes['mode']) this.initMode();
    if (changes['required']) this.setValidators();
  }

  ngAfterViewInit(): void {
    this.initMode();
    this.setupScrollListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupScrollListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const close = (event: Event) => {
        const panel = document.querySelector('.mat-mdc-autocomplete-panel');
        if (panel?.contains(event.target as Node)) return;
        this.ngZone.run(() => {
          if (this.trigger?.panelOpen) this.trigger.closePanel();
        });
      };
      document.addEventListener('scroll', close, { passive: true, capture: true });
      this.destroy$.subscribe(() => {
        document.removeEventListener('scroll', close, { capture: true });
      });
    });
  }

  initMode(): void {
    this.setValidators();
    this.refreshOpciones();
    this.inputControl.valueChanges.subscribe(() => {
      this.checkOption(this.inputControl.value, this.aOpciones);
    });
    if (this.mode === 'read') {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  private refreshOpciones(): void {
    this.opcionesfiltradas = this.inputControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '', this.aOpciones)),
    );
  }

  setValidators(): void {
    if (this.required) {
      this.inputControl.setValidators([Validators.required]);
    } else {
      this.inputControl.clearValidators();
    }
    this.inputControl.updateValueAndValidity();
  }

  mostrarDropdrown(sCodigo: string): string {
    if (!sCodigo) return '';
    for (const group of (this.aOpciones as Group[])) {
      const opcion = group.groupOptions.find(o => o.sCodigo === sCodigo);
      if (opcion) return opcion.sDescripcion;
    }
    return '';
  }

  _filter(value: any, aOpciones: Group[]): Group[] {
    const filterValue = this.normalizeFilterValue(value);

    if (aOpciones?.length > 0) {
      this.placeholder = this.label;
      return aOpciones
        .map((group: Group) => this.filterGroup(group, filterValue))
        .filter(x => x.bVisible === true || x.bVisible === undefined);
    }

    this.placeholder = "El listado de " + this.label + " está vacio";
    return [];
  }

  private normalizeFilterValue(value: any): string {
    if (typeof value === "string") {
      return value.toLowerCase();
    } else if (typeof value === "object" && value) {
      return value.sDescripcion?.toLowerCase() || "";
    }
    return "";
  }

  private filterGroup(group: Group, filterValue: string): Group {
    return {
      groupName: group.groupName,
      groupValue: group.groupValue,
      groupOptions: this.filterOptions(group.groupOptions, filterValue),
      bVisible: group.bVisible,
    };
  }

  filterOptions(opt: any[], value: string): any[] {
    return opt.filter(item => item.sDescripcion.toLowerCase().includes(value));
  }

  checkOption(value: any, aOpciones: Group[]): void {
    const aCodigos: any[] = this.extractCodigos(aOpciones);
    if (!aCodigos.includes(value) && this.required) {
      this.inputControl.setErrors({ invalid: true });
    }
  }

  private extractCodigos(aOpciones: Group[]): any[] {
    const aCodigos: any[] = [];
    for (const group of aOpciones || []) {
      if (group?.groupOptions) {
        for (const opcion of group.groupOptions) {
          aCodigos.push(opcion.sCodigo);
        }
      }
    }
    return aCodigos;
  }

  get IconOnInput() {
    if (this.inputControl.valid || this.inputControl.disabled) {
      const iconPpal = this.findOptionByCodigo(this.inputControl.value);
      return iconPpal?.icon ?? false;
    }
    return false;
  }

  private findOptionByCodigo(codigo: string): any {
    for (const group of (this.aOpciones || [])) {
      const opcion = group.groupOptions?.find((opt: Option) => opt.sCodigo === codigo);
      if (opcion) return opcion;
    }
    return null;
  }
}